import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canAccess, ROLE_LABELS } from "@/lib/roles";
import { BRAND_LOGO_URL, BRAND_NAME } from "@/lib/brand";
import {
    House, Package, ShoppingCart, Truck, Users, Storefront,
    ChartBar, Warning, ListChecks, Tag, SignOut, List, X, UsersThree, Wallet,
    FolderOpen, ClipboardText,
} from "@phosphor-icons/react";

const NAV = [
    { key: "dashboard", to: "/",           label: "Dashboard",   Icon: House },
    { key: "products",  to: "/products",   label: "Products",    Icon: Package },
    { key: "categories",to: "/categories", label: "Categories",  Icon: Tag },
    { key: "inventory", to: "/inventory",  label: "Inventory",   Icon: ListChecks },
    { key: "lowstock",  to: "/low-stock",  label: "Low Stock",   Icon: Warning },
    { key: "orders",    to: "/orders",     label: "Orders",      Icon: ClipboardText },
    { key: "sales",     to: "/sales",      label: "Sales",       Icon: ShoppingCart },
    { key: "purchases", to: "/purchases",  label: "Purchases",   Icon: Truck },
    { key: "payments",  to: "/payments",   label: "Payments",    Icon: Wallet },
    { key: "customers", to: "/customers",  label: "Customers",   Icon: Users },
    { key: "suppliers", to: "/suppliers",  label: "Suppliers",   Icon: Storefront },
    { key: "files",     to: "/files",      label: "Files",       Icon: FolderOpen },
    { key: "users",     to: "/users",      label: "Team",        Icon: UsersThree },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const visibleNav = NAV.filter((item) => canAccess(user, item.key));

    return (
        <div className="min-h-screen flex bg-[#FAFAFA]">
            {/* Sidebar - Desktop */}
            <aside
                className="hidden md:flex md:w-64 md:flex-col fixed inset-y-0 left-0 z-40"
                style={{ backgroundColor: "#0A0A0A" }}
                data-testid="sidebar-desktop"
            >
                <SidebarInner nav={visibleNav} user={user} onLogout={handleLogout} />
            </aside>

            {/* Sidebar - Mobile drawer */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 md:hidden" data-testid="sidebar-mobile">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
                    <aside className="absolute inset-y-0 left-0 w-64 flex flex-col" style={{ backgroundColor: "#0A0A0A" }}>
                        <SidebarInner nav={visibleNav} user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
                    </aside>
                </div>
            )}

            {/* Main content area */}
            <div className="flex-1 flex flex-col md:ml-64 min-w-0">
                {/* Top bar */}
                <header className="sticky top-0 z-30 h-14 md:h-16 bg-white/85 backdrop-blur-xl border-b border-neutral-200 flex items-center px-4 md:px-8">
                    <button
                        className="md:hidden mr-3 p-2 -ml-2"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Open menu"
                        data-testid="sidebar-open-btn"
                    >
                        <List size={22} weight="bold" />
                    </button>
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-neutral-500 hidden sm:block">
                            BS Fiber Product · ERP
                        </div>
                    </div>
                    {user && (
                        <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                                <div className="text-sm font-semibold text-neutral-900 font-display leading-tight" data-testid="topbar-user-name">
                                    {user.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-neutral-500" data-testid="topbar-user-role">
                                    {ROLE_LABELS[user.role]}
                                </div>
                            </div>
                            <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                style={{ backgroundColor: "#15803D" }}
                                data-testid="topbar-avatar"
                            >
                                {user.name?.[0]?.toUpperCase() || "U"}
                            </div>
                        </div>
                    )}
                </header>
                <main className="flex-1 p-4 md:p-8 min-w-0" data-testid="main-content">
                    {children}
                </main>
            </div>
        </div>
    );
}

function SidebarInner({ nav, user, onLogout, onNavigate }) {
    return (
        <>
            <div className="h-14 md:h-16 flex items-center px-4 border-b border-white/5">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                        <img src={BRAND_LOGO_URL} alt={BRAND_NAME} className="w-full h-full object-contain" />
                    </div>
                    <div>
                        <div className="text-white text-sm font-bold font-display leading-none">BS Fiber</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">Product ERP</div>
                    </div>
                </div>
                {onNavigate && (
                    <button className="ml-auto text-white/60 hover:text-white" onClick={onNavigate} data-testid="sidebar-close-btn">
                        <X size={20} />
                    </button>
                )}
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {nav.map(({ to, label, Icon, key }) => (
                    <NavLink
                        key={key}
                        to={to}
                        end={to === "/"}
                        className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}
                        onClick={onNavigate}
                        data-testid={`nav-link-${key}`}
                    >
                        <Icon size={18} weight="duotone" />
                        <span>{label}</span>
                    </NavLink>
                ))}
            </nav>
            <div className="p-3 border-t border-white/5">
                <button
                    onClick={onLogout}
                    className="sidebar-link w-full"
                    data-testid="sidebar-logout-btn"
                >
                    <SignOut size={18} weight="duotone" />
                    <span>Sign out</span>
                </button>
            </div>
        </>
    );
}
